'use client';

import { memo, useCallback, useEffect, useRef } from 'react';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { MagicStudentDisabledBanner } from '@/components/MagicStudentDisabledBanner';
import MaintenanceBanner from '@/components/MaintenanceBanner';
import TosUpdateBanner from '@/components/TosUpdateBanner';
import ActivatedTrialBanner from '@/components/trials/ActivatedTrialBanner';
import { useStore } from '@/features/store';
import { useResponsiveElementSize } from '@/hooks/useResponsiveElementSize';
import { getCookieValue } from '@/util/cookies';
import { IMPERSONATING_COOKIE, isImpersonating } from '@/util/impersonation';
import { PageBannerRegion } from './PageBannerRegion';

export type PageAlertsProps = {
  hideMaintenanceBanner?: boolean;
  hideTosUpdateBanner?: boolean;
  hideActivatedTrialBanner?: boolean;
  hideMagicStudentDisabledBanner?: boolean;
};

export const PageAlerts = memo(
  ({
    hideMaintenanceBanner = false,
    hideTosUpdateBanner = false,
    hideActivatedTrialBanner = false,
    hideMagicStudentDisabledBanner = false,
  }: PageAlertsProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const {
      handleResize: handleResizeResponsive,
      size: { height },
    } = useResponsiveElementSize(ref);
    const setField = useStore(({ LayoutStoreData: s }) => s.setField, []);
    const impersonationMode = isImpersonating(getCookieValue(IMPERSONATING_COOKIE));

    const handleResize = useCallback(() => handleResizeResponsive(0), [handleResizeResponsive]);

    useEffect(() => {
      setField('bannerHeight')(height);
    }, [height, setField]);

    if (
      [hideMaintenanceBanner, hideTosUpdateBanner, hideActivatedTrialBanner, hideMagicStudentDisabledBanner].every(Boolean) &&
      !impersonationMode
    ) {
      return null;
    }

    return (
      <PageBannerRegion ref={ref} className="fixed top-0 left-0 right-0 z-1000">
        <ImpersonationBanner />
        {!hideMaintenanceBanner && <MaintenanceBanner onDismiss={handleResize} />}
        {!hideActivatedTrialBanner && <ActivatedTrialBanner onDismiss={handleResize} />}
        {!hideTosUpdateBanner && <TosUpdateBanner onDismiss={handleResize} />}
        {!hideMagicStudentDisabledBanner && <MagicStudentDisabledBanner onDismiss={handleResize} />}
      </PageBannerRegion>
    );
  },
);
